import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled/macro'
import BundleCartItem from './BundleCartItem'
import { Link } from 'react-router-dom'
import { Icon } from '@paypalcorp/pp-react'
import { Text } from '../text'
import { Button } from '../button'
import { Box, FlexBox } from '../box'
import { Loader, Overlay } from '../loader'
import DisplayAmount from '../display-amount'
import { bundleCartProps } from '../../utils/hooks/useBundleCart'
import { getFirstItemCurrencyCode } from '../../utils/get-currency-code'
import { Message } from '@paypalcorp/worldready-react'
import _ from 'lodash'

const BundleCartContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
  background: ${({ theme }) => theme.colors.primary.white};
  z-index: 10010;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1), 0 -4px 8px -2px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(0, 0, 0, 0.1);
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  width: 100%;
  max-width: 32rem;
  text-align: left;
  min-height: 132px;
  display: flex;
  flex-flow: column;
  overflow: hidden;
  overflow-y: scroll;
  max-height: 100vh;
  padding: 2rem 0;
`

const HeaderText = styled(Text)`
  white-space: nowrap;
`

const CloseIcon = styled(Icon)`
  cursor: pointer;
`

const MiniCard = styled.img`
  width: 2rem;
  border-radius: 0.125rem;
  margin: 0.5rem 0.25rem 0;
`

const BundleCart = ({ cart }) => {
  const [isOpen, setOpen] = useState(false)

  function toggleDrawer() {
    setOpen(!isOpen)
  }

  const UpdatingOverlay = () => (
    <Overlay>
      <Box m="auto">
        {isOpen && <Loader />}
        <Text textStyle="h5" mt={isOpen ? 'x10' : 0}>
          <Message id="pages/custom-bundles.cart.updating" />
        </Text>
      </Box>
    </Overlay>
  )

  useEffect(() => {
    if (cart.items.length === 0) setOpen(false)
  }, [cart.items])

  if (cart.items.length === 0 && cart.updating) {
    return (
      <BundleCartContainer>
        <UpdatingOverlay />
      </BundleCartContainer>
    )
  }

  if (cart.items.length === 0) return null

  return (
    <BundleCartContainer>
      <FlexBox pb="x3" px="x3" alignItems="center">
        <HeaderText as="div" textStyle="h5" pr="x1">
          <Message id="pages/custom-bundles.cart.header" />
        </HeaderText>

        {isOpen ? (
          <Text as="div" textStyle="smallCaption" pl={['x1', 'x2']}>
            ({cart.items.length}{' '}
            <Message id="pages/custom-bundles.cart.itemLabel" />)
          </Text>
        ) : (
          <FlexBox alignItems="center" flexWrap="wrap" ml="auto" mt="-0.5rem">
            {cart.items.map((item) => (
              <MiniCard key={`img-${item.sku}`} src={item.imgUrl} />
            ))}
          </FlexBox>
        )}

        <Text
          pl="x1"
          color="primary.black"
          as="div"
          textStyle="h5"
          ml={isOpen ? 'auto' : 'x1'}
        >
          <DisplayAmount
            amountInCents={cart.total}
            currencyCode={getFirstItemCurrencyCode(cart.items)}
          />
        </Text>

        {isOpen && (
          <Box ml="x6">
            <CloseIcon name="close" size="xs" onClick={toggleDrawer} />
          </Box>
        )}
      </FlexBox>

      {isOpen ? (
        <Box pb="x2">
          {cart.items.map((item) => (
            <BundleCartItem
              key={`bundle-cart-item-${item.sku}`}
              item={item}
              inset
              handleRemove={() => cart.remove(item)}
            />
          ))}
        </Box>
      ) : null}

      <FlexBox px="x3" justifyContent="space-between">
        <Box pr="x1" flexGrow="1">
          <Button
            width="100%"
            variant="small"
            onClick={toggleDrawer}
            disabled={isOpen}
          >
            <Message id="pages/custom-bundles.cart.edit" />
          </Button>
        </Box>
        <Box pl="x1" flexGrow="1">
          <Link exact="true" to="/byob/add">
            <Button width="100%" variant="smallSecondary">
              <Message id="pages/custom-bundles.cart.checkout" />
            </Button>
          </Link>
        </Box>
      </FlexBox>
      {cart.updating && <UpdatingOverlay />}
    </BundleCartContainer>
  )
}

BundleCart.propTypes = {
  cart: bundleCartProps,
}

export default BundleCart
